import React, { useState, useEffect } from 'react';
import { InputComponent } from '../custcomponents';
import DataTable from 'react-data-table-component';
import { toast } from 'react-toastify';
import { AxiosAdminInstance } from '../utils';

interface AudienceExperience {
    id: string;
    caption: string;
    youtubeUrl: string;
    createdate: string;
}

export const ManageAudienceExperience = () => {
    const [audienceItems, setAudienceItems] = useState<AudienceExperience[]>([]);
    const [loading, setLoading] = useState(false);
    const [editingItem, setEditingItem] = useState<AudienceExperience | null>(null);
    const [newItem, setNewItem] = useState({
        caption: '',
        youtubeUrl: ''
    });

    useEffect(() => {
        fetchAudienceItems();
    }, []);

    useEffect(() => {
        if (editingItem) {
            setNewItem({
                caption: editingItem.caption,
                youtubeUrl: editingItem.youtubeUrl
            });
        } else {
            setNewItem({
                caption: '',
                youtubeUrl: ''
            });
        }
    }, [editingItem]);

    const fetchAudienceItems = async () => {
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.get('/api/admin/manageAudienceExperience?admin=true');
            if (response.data.result === 'Success') {
                // Ensure we always have an array, even if the API returns an empty object
                const data = response.data.data;
                const audienceArray = Array.isArray(data) ? data : Object.values(data || {});
                setAudienceItems(audienceArray);
            } else {
                toast.error(response.data.msg || 'Failed to fetch audience experience videos');
            }
        } catch (error) {
            toast.error('Failed to fetch audience experience videos');
        }
        setLoading(false);
    };

    const handleAddAudienceItem = async () => {
        if (!newItem.caption.trim()) {
            toast.error('Caption is required');
            return;
        }
        if (!newItem.youtubeUrl.trim()) {
            toast.error('YouTube URL is required');
            return;
        }
        
        // Validate YouTube URL format
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/;
        if (!youtubeRegex.test(newItem.youtubeUrl)) {
            toast.error('Please enter a valid YouTube URL');
            return;
        }

        setLoading(true);
        try {
            let data: any = {
                caption: newItem.caption.trim(),
                youtubeUrl: newItem.youtubeUrl.trim()
            };

            if (editingItem) {
                data.experienceId = editingItem.id;
            }

            let response;
            if (editingItem) {
                response = await AxiosAdminInstance.put('/api/admin/manageAudienceExperience', data);
            } else {
                response = await AxiosAdminInstance.post('/api/admin/manageAudienceExperience', data);
            }

            if (response.data.result === 'Success') {
                toast.success(editingItem ? 'Audience experience video updated successfully' : 'Audience experience video added successfully');
                setNewItem({
                    caption: '',
                    youtubeUrl: ''
                });
                setEditingItem(null);
                fetchAudienceItems();
            } else {
                toast.error(response.data.msg || 'Operation failed');
            }
        } catch (error) {
            toast.error(editingItem ? 'Failed to update audience experience video' : 'Failed to add audience experience video');
        }
        setLoading(false);
    };

    const handleCancel = () => {
        setNewItem({
            caption: '',
            youtubeUrl: ''
        });
        setEditingItem(null);
    };

    const handleDeleteAudienceItem = async (experienceId: string) => {
        if (!window.confirm('Are you sure you want to delete this audience experience video?')) return;
        setLoading(true);
        try {
            const response = await AxiosAdminInstance.delete('/api/admin/manageAudienceExperience', {
                params: { experienceId }
            });
            if (response.data.result === 'Success') {
                toast.success('Audience experience video deleted successfully');
                fetchAudienceItems();
            } else {
                toast.error(response.data.msg || 'Failed to delete audience experience video');
            }
        } catch (error) {
            toast.error('Failed to delete audience experience video');
        }
        setLoading(false);
    };

    const handleDeleteClick = (e: React.MouseEvent, experienceId: string) => {
        e.preventDefault();
        e.stopPropagation();
        handleDeleteAudienceItem(experienceId);
    };

    const audienceColumns = [
        { 
            name: 'Actions', 
            cell: (row: AudienceExperience) => (
                <div>
                    <button className="btn btn-sm btn-warning me-1" onClick={() => setEditingItem(row)} title="Edit">
                        <i className="fa fa-edit"></i>
                    </button>
                    <button className="btn btn-sm btn-danger" onClick={(e) => handleDeleteClick(e, row.id)} title="Delete">
                        <i className="fa fa-trash"></i>
                    </button>
                </div>
            ) 
        },
        { 
            name: 'Caption', 
            selector: (row: AudienceExperience) => row.caption, 
            sortable: true,
            width: '250px'
        },
        { 
            name: 'Date', 
            selector: (row: AudienceExperience) => new Date(row.createdate).toLocaleDateString(), 
            sortable: true,
            width: '150px'
        },
        { 
            name: 'YouTube Preview', 
            cell: (row: AudienceExperience) => (
                <div style={{ width: '120px', height: '67px', overflow: 'hidden', borderRadius: '4px' }}>
                    {row.youtubeUrl ? (
                        <iframe
                            src={row.youtubeUrl.replace('embed/', 'embed/').replace('watch?v=', 'embed/')}
                            width="120"
                            height="67"
                            frameBorder="0"
                            allowFullScreen
                            title={`${row.caption} Preview`}
                        ></iframe>
                    ) : 'No Video'}
                </div>
            ), 
            sortable: false,
            width: '140px'
        },
        { 
            name: 'YouTube URL', 
            cell: (row: AudienceExperience) => (
                row.youtubeUrl ? (
                    <a href={row.youtubeUrl} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info" style={{ fontSize: '12px', padding: '2px 6px' }}>
                        View Video
                    </a>
                ) : 'No URL'
            ), 
            sortable: false,
            width: '120px'
        }
    ];

    return (
        <>
            <section className="register-sec sec-padding-admin" data-scroll-index="8">
                <div className="container">
                    <div className="sec-title">
                        <span className="icon-inner wow flipInY" data-wow-duration="300ms">
                            <span className="fa-stack">
                                <i className="fa rhex fa-stack-2x"></i>
                                <i className="fa fa-play fa-stack-1x"></i>
                            </span>
                        </span>
                        <h3 className="wow fadeInRight" data-wow-duration="500ms">Manage Audience Experiences</h3>
                    </div>
                    <div className="register-sec-details">
                        <form onSubmit={(e) => { e.preventDefault(); handleAddAudienceItem(); }}>
                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">{editingItem ? 'Edit Audience Experience Video' : 'Add New Audience Experience Video'}</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="caption"
                                            custplaceholder="Video Caption"
                                            custid="audienceCaption"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, caption: val }))}
                                            onExternalBind={newItem.caption}
                                        />
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-12">
                                        <InputComponent
                                            custname="youtubeUrl"
                                            custplaceholder="YouTube URL (Required)"
                                            custid="audienceYoutubeUrl"
                                            onExternalValue={(val: string) => setNewItem(prev => ({ ...prev, youtubeUrl: val }))}
                                            onExternalBind={newItem.youtubeUrl}
                                        />
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <small className="text-muted">
                                            Supported YouTube URL formats: 
                                            https://www.youtube.com/watch?v=VIDEO_ID, 
                                            https://youtu.be/VIDEO_ID, 
                                            https://www.youtube.com/embed/VIDEO_ID
                                        </small>
                                    </div>
                                </div>
                                <div className="row pt-2">
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        <button className="btn-default" type="submit" disabled={loading}>
                                            {editingItem ? 'Update Video' : 'Add Video'} {loading && <i className="fa fa-circle-o-notch fa-spin"></i>}
                                        </button>
                                    </div>
                                    <div className="col-lg-6 col-md-6 col-sm-6">
                                        {editingItem && (
                                            <button className="btn-default" type="button" onClick={handleCancel}>
                                                Cancel
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </fieldset>

                            <fieldset className="form-group p-3">
                                <legend className="w-auto px-2 subheading">Audience Experience Videos</legend>
                                <div className="row pt-2">
                                    <div className="col-lg-12 col-md-12 col-sm-12">
                                        <DataTable
                                            columns={audienceColumns}
                                            data={audienceItems}
                                            customStyles={{
                                                cells: {
                                                    style: {
                                                        wordWrap: 'break-word',
                                                        whiteSpace: 'normal',
                                                        padding: '8px'
                                                    }
                                                }
                                            }}
                                            pagination
                                            paginationPerPage={10}
                                            paginationRowsPerPageOptions={[5, 10, 25, 50]}
                                        />
                                    </div>
                                </div>
                            </fieldset>
                        </form>
                    </div>
                </div>
            </section>
        </>
    );
};